import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Reveal, Overline } from "./Reveal";
import { submitInquiry } from "../lib/api";
import { Check } from "lucide-react";

const inputCls =
  "w-full bg-transparent border-0 border-b border-ivory/25 rounded-none px-0 py-3 font-sans font-light text-ivory text-base placeholder:text-ivory/40 focus:outline-none focus:border-gold transition-colors duration-300";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await submitInquiry(form);
      setDone(true);
      toast.success("Message sent. We will be in touch.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-navy py-28 md:py-40"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 lg:gap-20">
        <div className="md:col-span-5">
          <Reveal>
            <Overline>Private Enquiries</Overline>
            <h2 className="mt-6 font-serif font-light text-ivory text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              A conversation,
              <br />
              <span className="italic text-gold">in confidence.</span>
            </h2>
            <p className="mt-8 font-sans font-light text-ivory/70 leading-relaxed max-w-sm">
              For press, existing members, and general enquiries. For membership,
              please use the application above.
            </p>
            <div className="mt-10 space-y-2 font-sans font-light text-ivory/80">
              <p className="text-gold text-xs tracking-[0.2em] uppercase">Correspond</p>
              <p>concierge@theeighthkey.in</p>
              <p>London · Dubai · Singapore · New York · India</p>
            </div>
          </Reveal>
        </div>

        <div className="md:col-span-6 md:col-start-7">
          <Reveal delay={0.1}>
            {done ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col items-start gap-5 py-6"
                data-testid="contact-success"
              >
                <span className="flex h-14 w-14 items-center justify-center rounded-full border border-gold text-gold">
                  <Check size={26} strokeWidth={1.5} />
                </span>
                <h3 className="font-serif font-light text-ivory text-3xl tracking-tight">
                  Thank you.
                </h3>
                <p className="font-sans font-light text-ivory/70 max-w-md leading-relaxed">
                  Your message has reached us. We will respond with the discretion
                  it deserves.
                </p>
              </motion.div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="space-y-8">
                <div className="grid sm:grid-cols-2 gap-8">
                  <input
                    data-testid="contact-name-input"
                    className={inputCls}
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Name"
                  />
                  <input
                    data-testid="contact-email-input"
                    type="email"
                    className={inputCls}
                    value={form.email}
                    onChange={update("email")}
                    placeholder="Email"
                  />
                </div>
                <input
                  data-testid="contact-subject-input"
                  className={inputCls}
                  value={form.subject}
                  onChange={update("subject")}
                  placeholder="Subject"
                />
                <textarea
                  data-testid="contact-message-input"
                  rows={4}
                  className={inputCls + " resize-none"}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Your message"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-btn"
                  className="group inline-flex items-center gap-3 rounded-full bg-gold px-9 py-4 text-xs uppercase tracking-[0.18em] text-navy hover:bg-goldSubtle transition-colors duration-300 disabled:opacity-60"
                >
                  {loading ? "Sending..." : "Send Message"}
                  <span className="h-1.5 w-1.5 rounded-full bg-navy transition-transform duration-300 group-hover:scale-150" />
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};
